import { useState, useMemo } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
  GET_PRENOMINA_INDICATORS,
  GET_PRENOMINA_NUEVOS_INGRESOS,
  GET_PRENOMINA_BAJAS,
  GET_PRENOMINA_PENDIENTES_PAGO,
  GET_PRENOMINA_INCID_TODAS,
} from '../../Querys/querys';
import { normalizePrenominaStatus } from './helpers/incidentStatus';

type DrillId = 'altas' | 'pendientes' | 'bajas' | 'incidencias' | null;

const POLLING_MS = 30000;
const PAGE_SIZE = 10;

const PrenominaIndicators = () => {
  const [drill, setDrill] = useState<DrillId>(null);
  const [page, setPage] = useState(0);

  const { data: countsData } = useQuery(GET_PRENOMINA_INDICATORS, {
    pollInterval: POLLING_MS,
    fetchPolicy: 'cache-and-network',
  });
  const counts = countsData?.GET_PRENOMINA_INDICATORS ?? {
    nuevosIngresos: 0,
    pendientesPago: 0,
    bajas: 0,
    incidenciasPendientes: 0,
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [loadAltas, { data: altasData, loading: loadingAltas }] = useLazyQuery(GET_PRENOMINA_NUEVOS_INGRESOS, { fetchPolicy: 'cache-and-network' });
  const [loadBajas, { data: bajasData, loading: loadingBajas }] = useLazyQuery(GET_PRENOMINA_BAJAS, { fetchPolicy: 'cache-and-network' });
  const [loadPendientes, { data: pendientesData, loading: loadingPendientes }] = useLazyQuery(GET_PRENOMINA_PENDIENTES_PAGO, { fetchPolicy: 'cache-and-network' });
  const { data: incidenciasData, loading: loadingIncidencias, error: incidenciasError } = useQuery(GET_PRENOMINA_INCID_TODAS, { fetchPolicy: 'cache-and-network' });

  const toggleDrill = (id: DrillId) => {
    if (drill === id) {
      setDrill(null);
      return;
    }
    setDrill(id);
    setPage(0);
    setSearchTerm('');
    setSortCol(null);
    setSortDir('asc');
    if (id === 'altas') loadAltas();
    if (id === 'bajas') loadBajas();
    if (id === 'pendientes') loadPendientes();
  };

  const incidenciasTotal = loadingIncidencias && !incidenciasData
    ? null
    : (incidenciasData?.GET_PRENOMINA_INCID_TODAS ?? []).length;

  const indicadores = [
    {
      id: 'altas' as DrillId,
      kicker: 'ALTAS',
      label: 'Nuevos ingresos del periodo',
      valor: counts.nuevosIngresos ?? 0,
    },
    {
      id: 'pendientes' as DrillId,
      kicker: 'ADEUDOS',
      label: 'Colaboradores pendientes de pago',
      valor: counts.pendientesPago ?? 0,
    },
    {
      id: 'bajas' as DrillId,
      kicker: 'BAJAS',
      label: 'Bajas del periodo',
      valor: counts.bajas ?? 0,
    },
    {
      id: 'incidencias' as DrillId,
      kicker: 'INCIDENCIAS DEL PERIODO',
      label: 'Incidencias pendientes',
      valor: incidenciasTotal ?? 0,
    },
  ];

  const drillConfig: Record<string, { titulo: string; columna: string }> = {
    altas: { titulo: 'Nuevos ingresos del periodo', columna: 'Fecha de ingreso' },
    pendientes: { titulo: 'Colaboradores pendientes de pago', columna: 'Periodos adeudados' },
    bajas: { titulo: 'Bajas del periodo', columna: 'Fecha de baja' },
    incidencias: { titulo: 'Incidencias del periodo', columna: 'Tipo' },
  };

  const getDrillFilas = () => {
    if (drill === 'altas') return (altasData?.GET_PRENOMINA_NUEVOS_INGRESOS ?? []) as any[];
    if (drill === 'bajas') return (bajasData?.GET_PRENOMINA_BAJAS ?? []) as any[];
    if (drill === 'pendientes') return (pendientesData?.GET_PRENOMINA_PENDIENTES_PAGO ?? []) as any[];
    if (drill === 'incidencias') return (incidenciasData?.GET_PRENOMINA_INCID_TODAS ?? []) as any[];
    return [];
  };

  const isLoadingDrill =
    (drill === 'altas' && loadingAltas) ||
    (drill === 'bajas' && loadingBajas) ||
    (drill === 'pendientes' && loadingPendientes) ||
    (drill === 'incidencias' && loadingIncidencias);

  const fmtDate = (val: any): string => {
    if (!val) return '';
    const ms = Number(val);
    if (!isNaN(ms) && ms > 1000000000000) {
      const d = new Date(ms);
      // El SP devuelve DATE (medianoche UTC); formatear en UTC para no restar
      // un día en zonas negativas como México (UTC-6). Ver bug "un día menos".
      return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
    }
    return String(val);
  };

  const sinAcentos = (s: string) =>
    s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const formatDetalle = (fila: any): string => {
    if (drill === 'altas' || drill === 'bajas') return fmtDate(fila.fecha);
    if (drill === 'pendientes') {
      return fila.periodoInit && fila.fecha
        ? `${fmtDate(fila.periodoInit)} – ${fmtDate(fila.fecha)}`
        : fmtDate(fila.fecha);
    }
    return '';
  };

  const filas = getDrillFilas();

  const handleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPage(0);
  };

  const filasProcesadas = useMemo(() => {
    let r = filas as any[];
    if (drill === 'incidencias' && searchTerm.trim()) {
      const q = sinAcentos(searchTerm.trim());
      r = r.filter((f: any) =>
        sinAcentos(f.colaborador ?? '').includes(q) ||
        sinAcentos(f.concepto ?? '').includes(q) ||
        sinAcentos(f.grupo ?? '').includes(q) ||
        sinAcentos(normalizePrenominaStatus(f).label).includes(q)
      );
    }
    if (drill === 'incidencias' && sortCol) {
      r = [...r].sort((a: any, b: any) => {
        let va: any, vb: any;
        if (sortCol === 'estatus') {
          va = normalizePrenominaStatus(a).label;
          vb = normalizePrenominaStatus(b).label;
        } else if (sortCol === 'fecha') {
          va = Number(a.fecha) || 0;
          vb = Number(b.fecha) || 0;
          return sortDir === 'asc' ? va - vb : vb - va;
        } else {
          va = String(a[sortCol] ?? '');
          vb = String(b[sortCol] ?? '');
        }
        return sortDir === 'asc'
          ? String(va).localeCompare(String(vb), 'es-MX')
          : String(vb).localeCompare(String(va), 'es-MX');
      });
    }
    return r;
  }, [filas, drill, searchTerm, sortCol, sortDir]);

  const totalPages = Math.ceil(filasProcesadas.length / PAGE_SIZE);
  const filasPagina = filasProcesadas.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ─── Estilos (inline, sin dependencia de CSS externo) ──────────────────────

  const cardBase: React.CSSProperties = {
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 1px 3px rgba(18,57,92,0.09)',
    padding: '24px 28px 28px',
    marginBottom: 20,
    fontFamily: "'Open Sans', sans-serif",
    color: '#33475b',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 18,
  };

  const indicadorCard = (selected: boolean): React.CSSProperties => ({
    border: `1px solid ${selected ? '#f5b800' : '#e3e9f0'}`,
    background: selected ? '#fffdf5' : '#fff',
    borderRadius: 8,
    padding: 18,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    alignItems: 'flex-start',
  });

  const kickerStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#6b7c93',
  };

  const numStyle = (valor: number): React.CSSProperties => ({
    fontSize: 40,
    fontWeight: 700,
    lineHeight: 1.05,
    color: valor === 0 ? '#c9d3de' : '#12395c',
  });

  const labelStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: '#12395c',
  };

  const linkStyle = (valor: number): React.CSSProperties => ({
    marginTop: 8,
    background: 'none',
    border: 0,
    padding: 0,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: valor === 0 ? 'default' : 'pointer',
    color: valor === 0 ? '#a8b4c2' : '#1e6fa8',
    fontFamily: 'inherit',
  });

  const drillContainer: React.CSSProperties = {
    marginTop: 18,
    border: '1px solid #e3e9f0',
    borderRadius: 8,
    overflow: 'hidden',
  };

  const drillHeader: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: '#f8fafc',
    borderBottom: '1px solid #e3e9f0',
  };

  const drillTitle: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 700,
    color: '#12395c',
  };

  const btnGhost: React.CSSProperties = {
    background: 'none',
    border: '1px solid #cbd5e0',
    borderRadius: 6,
    padding: '6px 14px',
    fontSize: 13,
    color: '#33475b',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: 12,
    fontWeight: 700,
    color: '#6b7c93',
    background: '#f8fafc',
    borderBottom: '1px solid #e3e9f0',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '10px 14px',
    fontSize: 13.5,
    color: '#33475b',
    borderBottom: '1px solid #f0f3f7',
  };

  const tdMuted: React.CSSProperties = { ...tdStyle, color: '#8a99ad' };

  return (
    <div style={cardBase}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#12395c' }}>Indicadores del periodo en curso</div>
          <div style={{ fontSize: 13, color: '#6b7c93', marginTop: 2 }}>Todos los grupos de nómina activos</div>
        </div>
      </div>

      <div style={gridStyle}>
        {indicadores.map((ind) => {
          const selected = drill === ind.id;
          const esIncidencias = ind.id === 'incidencias';
          const pendientes = esIncidencias ? (counts.incidenciasPendientes ?? 0) : null;
          const canDrill = esIncidencias ? (pendientes ?? 0) > 0 : ind.valor > 0;
          return (
            <div key={ind.id} style={indicadorCard(selected)}>
              <div style={kickerStyle}>{ind.kicker}</div>
              <div style={numStyle(ind.valor)}>
                {esIncidencias && incidenciasTotal === null ? '—' : ind.valor}
              </div>
              <div style={labelStyle}>{ind.label}</div>
              {esIncidencias && (
                <div style={numStyle(pendientes ?? 0)}>{pendientes ?? 0}</div>
              )}
              <button
                type="button"
                style={linkStyle(canDrill ? 1 : 0)}
                onClick={() => canDrill && toggleDrill(ind.id)}
                disabled={!canDrill}
              >
                {!canDrill ? 'Sin pendientes' : selected ? 'Ocultar listado ↑' : 'Ver listado →'}
              </button>
            </div>
          );
        })}
      </div>

      {drill && (
        <div style={drillContainer}>
          <div style={drillHeader}>
            <div>
              <div style={drillTitle}>{drillConfig[drill].titulo}</div>
              {drill === 'incidencias' && !isLoadingDrill && (
                <div style={{ fontSize: 12, color: '#6b7c93', marginTop: 2 }}>
                  {counts.incidenciasPendientes} pendientes · {filas.length} en total
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {drill === 'incidencias' && (
                <input
                  type="text"
                  placeholder="Buscar…"
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
                  style={{
                    border: '1px solid #cbd5e0', borderRadius: 6, padding: '5px 10px',
                    fontSize: 13, color: '#33475b', fontFamily: 'inherit', outline: 'none',
                    width: 180,
                  }}
                />
              )}
              <button type="button" style={btnGhost} onClick={() => setDrill(null)}>
                Cerrar listado
              </button>
            </div>
          </div>

          {drill === 'incidencias' && incidenciasError ? (
            <div style={{ padding: '24px 16px', fontSize: 13.5, color: '#c0392b' }}>
              No se pudieron cargar las incidencias. Intenta recargar la página.
            </div>
          ) : isLoadingDrill ? (
            <div style={{ padding: '24px 16px', fontSize: 13.5, color: '#8a99ad' }}>Cargando…</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                  <thead>
                    <tr>
                      {drill === 'incidencias' ? (
                        <>
                          {(['colaborador', 'tipo', 'concepto', 'fecha', 'estatus'] as const).map(col => (
                            <th
                              key={col}
                              style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => handleSort(col)}
                            >
                              {col === 'colaborador' ? 'Colaborador'
                                : col === 'tipo' ? 'Categoría'
                                : col === 'concepto' ? 'Concepto'
                                : col === 'fecha' ? 'Fecha'
                                : 'Estatus'}
                              {sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                            </th>
                          ))}
                          <th
                            key="grupo"
                            style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => handleSort('grupo')}
                          >
                            Grupo de nómina{sortCol === 'grupo' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                          </th>
                        </>
                      ) : (
                        <>
                          <th style={thStyle}>Colaborador</th>
                          <th style={thStyle}>Grupo de nómina</th>
                          <th style={thStyle}>{drillConfig[drill].columna}</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filasProcesadas.length === 0 ? (
                      <tr>
                        <td colSpan={drill === 'incidencias' ? 6 : 3} style={{ ...tdStyle, textAlign: 'center', color: '#a8b4c2' }}>
                          Sin resultados
                        </td>
                      </tr>
                    ) : (
                      filasPagina.map((fila: any, i: number) => (
                        <tr key={i}>
                          {drill === 'incidencias' ? (
                            <>
                              <td style={tdStyle}>{fila.colaborador}</td>
                              <td style={tdStyle}>{fila.tipo === 'Tiempo' ? '↑' : '↓'} {fila.tipo ?? ''}</td>
                              <td style={tdStyle}>{fila.concepto ?? ''}</td>
                              <td style={tdStyle}>{fmtDate(fila.fecha)}</td>
                              <td style={tdStyle}>
                                {(() => {
                                  const s = normalizePrenominaStatus(fila);
                                  return (
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 10px',
                                      borderRadius: 12,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      background: s.color + '22',
                                      color: s.color,
                                      whiteSpace: 'nowrap',
                                    }}>
                                      {s.label}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td style={tdMuted}>
                                {fila.grupo
                                  ? fila.grupo
                                  : <span style={{ color: '#e8832a', fontWeight: 600, fontSize: 12 }}>Sin grupo</span>
                                }
                              </td>
                            </>
                          ) : (
                            <>
                              <td style={tdStyle}>{fila.colaborador}</td>
                              <td style={tdMuted}>
                                {fila.grupo
                                  ? fila.grupo
                                  : <span style={{ color: '#e8832a', fontWeight: 600, fontSize: 12 }}>Sin grupo</span>
                                }
                              </td>
                              <td style={tdStyle}>{formatDetalle(fila)}</td>
                            </>

                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '10px 16px', borderTop: '1px solid #f0f3f7' }}>
                  <span style={{ fontSize: 13, color: '#6b7c93' }}>
                    {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filasProcesadas.length)} de {filasProcesadas.length}
                  </span>
                  <button
                    type="button"
                    style={{ ...btnGhost, padding: '4px 10px', opacity: page === 0 ? 0.4 : 1, cursor: page === 0 ? 'default' : 'pointer' }}
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 0}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    style={{ ...btnGhost, padding: '4px 10px', opacity: page >= totalPages - 1 ? 0.4 : 1, cursor: page >= totalPages - 1 ? 'default' : 'pointer' }}
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PrenominaIndicators;
